import { useRef } from 'react';
import * as styles from './styles/hubspot-form.module.scss';
import Image from '@klv/atoms/image';
import { generateClassName } from '@klv/shared/utils/functions';
import useIsHydrated from '@klv/hooks/useIsHydrated';
import { useFormError } from './hooks/useFormError';
import { useCreateForm } from './hooks/useCreateForm';
import { useFormCreationTrigger } from './hooks/useFormCreationTrigger';
import { useHubSpotApiLoader } from './hooks/useHubSpotApiLoader';
import { useVerifyFormCreated } from './hooks/useVerifyFormCreated';

import type { IHubspotFormAtom } from './types';

const FORM_ID_PREFIX = 'hubspot-form-';

const HubspotFormAtom = ({
  hubspotHeaderText,
  hubspotSubheaderText,
  formId,
  image,
  redirectUrl,
  singleLineForm,
}: IHubspotFormAtom) => {
  const uniqueIdRef = useRef<string>(`${FORM_ID_PREFIX}${formId}`);
  const formCreated = useRef(false);
  const { isLoaded } = useHubSpotApiLoader();

  // Use the existing hydration hook
  const isHydrated = useIsHydrated();
  
  // Use the form error hook
  const { retryAttempt, handleFormError } = useFormError({ formCreated });

  const { createForm } = useCreateForm({
    formId,
    elementId: uniqueIdRef.current,
    redirectUrl,
    onFormError: handleFormError,
  });

  useFormCreationTrigger({
    isLoaded,
    isHydrated,
    formId,
    elementId: uniqueIdRef.current,
    formCreated,
    createForm,
  });

  // Verify form exists after creation
  useVerifyFormCreated({
    isLoaded,
    isHydrated,
    formCreated,
    elementId: uniqueIdRef.current,
    retryAttempt,
    createForm,
  });


  return (
    <div className={styles.gridContainer}>
      <div className={singleLineForm ? styles.singleLineForm : ''}>
        {hubspotHeaderText && <h4 className={generateClassName(['h4', 'semibold', styles.headline])}>{hubspotHeaderText}</h4>}
        {hubspotSubheaderText && <div className={generateClassName([styles.subHeadline])} dangerouslySetInnerHTML={{ __html: hubspotSubheaderText }} />}
        <div className={styles.hubspotForm} id={uniqueIdRef.current} />
      </div>
      {image?.gatsbyImage && <Image altText={image?.altText} imageData={image?.gatsbyImage} />}
    </div>
  );
};

export default HubspotFormAtom;
